/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

import useSWR, { Key, SWRConfiguration } from 'swr';

import { Route, routes } from 'config/routes';
import { api } from 'services/api';

interface Options extends SWRConfiguration {}

const fetcher = (searchParams?: string) => {
	return async (route: Route) => {
		const url = String(routes.get(route) ?? route);

		const external = url.startsWith('http');
		if (external) return fetch(url).then((r) => r.json());

		const [pathname, query] = url.split('?');

		const newQuery = [query, searchParams].uniq().compact().join('&');
		const newUrl = [pathname, newQuery].uniq().compact().join('?');

		return api.get(newUrl).then((response) => response.data);
	};
};

export function useFetch<T>(key: Key | Route, options?: Options) {
	// Hooks
	const searchParams = useSearchParams();

	// Memo vars
	const newKey = useMemo(() => {
		if (!key) return key;
		if (typeof key !== 'string') return key;

		return `${key.split('?')[0]}?${searchParams.toString()}`;
	}, [key, searchParams]);

	// SWR
	const response = useSWR<T>(newKey, fetcher(), {
		keepPreviousData: true,
		...(options ?? {}),
	});

	return response;
}
